/** Farben der Zeichenflächen (Cytoscape und die SVG-Bilder der Karten) – aus den Design-Tokens (styles/tokens.css). */
export interface Theme {
  surface: string;
  text: string;
  muted: string;
  border: string;
  accent: string;
  canvas: string;
  /** Zeitfähnchen: Füllung und Linie */
  flagFill: string;
  flagLine: string;
  /** Ablegen erlaubt bzw. nicht erlaubt */
  ok: string;
  danger: string;
  /** Status der Szenen (Fähnchen der Vorschaukarten): Entwurf bzw. überarbeitet */
  warn: string;
  info: string;
  dark: boolean;
}

/** Bereits umgerechnete Farbangaben (CSS-Wert → #rrggbb) */
const resolved = new Map<string, string>();
let probe: { el: HTMLElement; ctx: CanvasRenderingContext2D | null } | null = null;

const hex2 = (n: number): string => n.toString(16).padStart(2, '0');

/**
 * Eine CSS-Farbe als `#rrggbb`. Die Tokens sind aus drei Grundfarben gemischt (`color-mix`); Cytoscape und die Rechnungen mit
 * Farben (z. B. Tönungen der Vorschaukarten) brauchen einfache Werte. Der Browser rechnet die Farbe an einem unsichtbaren Element
 * aus, eine 1×1-Leinwand liefert die sRGB-Werte. Ohne Leinwand (Tests): nur einfache Werte, sonst `fallback`.
 */
export function resolveColor(value: string, fallback: string): string {
  const css = value.trim();
  if (!css) return fallback;
  if (/^#[0-9a-f]{6}$/i.test(css)) return css.toLowerCase();
  const known = resolved.get(css);
  if (known) return known;
  if (typeof document === 'undefined') return fallback;
  if (!probe) {
    const el = document.createElement('span');
    el.style.display = 'none';
    document.documentElement.appendChild(el);
    let ctx: CanvasRenderingContext2D | null = null;
    try {
      const canvas = document.createElement('canvas');
      canvas.width = canvas.height = 1;
      ctx = canvas.getContext('2d', { willReadFrequently: true });
    } catch {
      ctx = null;
    }
    probe = { el, ctx };
  }
  probe.el.style.color = '';
  probe.el.style.color = css;
  if (!probe.el.style.color) return fallback; // kein gültiger Farbwert
  const computed = getComputedStyle(probe.el).color;
  let out = fallback;
  const rgb = /^rgba?\((\d+),\s*(\d+),\s*(\d+)/.exec(computed);
  if (rgb) out = `#${hex2(Number(rgb[1]))}${hex2(Number(rgb[2]))}${hex2(Number(rgb[3]))}`;
  else if (probe.ctx) {
    const ctx = probe.ctx;
    ctx.clearRect(0, 0, 1, 1);
    ctx.fillStyle = fallback;
    ctx.fillStyle = computed;
    ctx.fillRect(0, 0, 1, 1);
    const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;
    out = `#${hex2(r ?? 0)}${hex2(g ?? 0)}${hex2(b ?? 0)}`;
  }
  resolved.set(css, out);
  return out;
}

export function readTheme(): Theme {
  const css = getComputedStyle(document.documentElement);
  const v = (name: string, fallback: string): string => resolveColor(css.getPropertyValue(name), fallback);
  return {
    surface: v('--surface', '#fbf9f5'),
    text: v('--text', '#2b2b2b'),
    muted: v('--text-muted', '#6f6b65'),
    border: v('--border-strong', '#b9b2a8'),
    accent: v('--accent', '#2b2b2b'),
    canvas: v('--canvas', '#f8f5ef'),
    flagFill: v('--flag-fill', '#fbf9f5'),
    flagLine: v('--flag-line', '#b9b2a8'),
    ok: v('--ok', '#4d7a57'),
    danger: v('--danger', '#a5452f'),
    warn: v('--warn', '#a8742a'),
    info: v('--info', '#4f6d8f'),
    dark: document.documentElement.dataset.theme === 'dark' || (window.matchMedia('(prefers-color-scheme: dark)').matches && document.documentElement.dataset.theme !== 'light'),
  };
}
